import React, { useEffect, useState } from 'react';
import { getLiveLayoutColors } from './RunOfShow/helper/presentationLive';
import { mapStyles } from './Presentation/main/Settings/MagicMapSettings';
import { updateEngagement, updateQuestion } from '@services/youtube-platform.service';
import { useStreamData } from '@lib/hooks/useStreamData';
import Tooltip from '@atoms/new/tooltip';
import TooltipBox from '@atoms/TooltipBox';
import { Popover, PopoverTrigger, PopoverContent } from '@heroui/popover';
import {
  ArrowsInIcon as CloseFullscreenRoundedIcon,
  StackIcon as LayersRoundedIcon,
  ArrowsOutIcon as OpenInFullRoundedIcon,
  PauseIcon as PauseRoundedIcon,
  PlayIcon as PlayArrowRoundedIcon,
  QrCodeIcon as QrCodeSharpIcon,
  ArrowCounterClockwiseIcon as ReplayIcon,
  TimerIcon as TimerOutlinedIcon
} from '@phosphor-icons/react';
import cloudVariants from '@components/Features/TransientThoughtCloudVariants';
import bubbleVariants from '@components/Features/TransientThoughtBubbleVariants';

const ActionToolbar = ({
  showPlayIcon,
  showPauseIcon,
  showReplayIcon,
  replayIconText,
  showResetPopup,
  handlePlayIcon,
  handlePauseIcon,
  handleReplayIcon,
  setShowTimer,
  isFullScreen,
  handleFullScreen,
  isZoomApp,
  showQrCode,
  handleQrCode,
  layoutTheme,
  showInstructions,
  currentRosFeature,
  setCurrentRosFeature,
  setLinkQuestions,
  showMapStylePopup,
  setShowMapStylePopup,
  showTTStylePopup,
  setShowTTStylePopup,
  layoutMode,
  showInstantCustomisationPopup,
  onboardingSettings,
  isDisabled = false,
  isPreview = false,
  isPowerpoint = false,
  isInIframe = false
}) => {
  const colors = getLiveLayoutColors(layoutTheme);

  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const DESIGN_WIDTH = 1440;

  const { featureId } = useStreamData(s => ({ featureId: s.featureId }));

  const mapStyle = currentRosFeature?.setting?.mapConfig?.style || 'Standard';

  async function onMapStyleChange(style) {
    const updatedSetting = {
      ...currentRosFeature.setting,
      mapConfig: {
        ...currentRosFeature.setting.mapConfig,
        style: style.value
      }
    };

    const updatedColor = {
      ...currentRosFeature.color,
      mapLandColor: style.styleColors.landColor,
      mapWaterColor: style.styleColors.waterColor,
    };

    setCurrentRosFeature((prev) => ({
      ...prev,
      setting: updatedSetting,
      color: updatedColor
    }));

    const response = await updateQuestion(currentRosFeature.id, { color: updatedColor, setting: updatedSetting });
    setShowMapStylePopup(false);

    if (isPreview) return;
    await updateEngagement(featureId, 'map', { brandColours: updatedColor, settings: updatedSetting });

    if (response.status) {
      setLinkQuestions((prevLinkQuestions) => {
        const updatedQuestions = prevLinkQuestions.map((prevLinkQuestion) => {
          const updatedLinkQuestion =
            prevLinkQuestion.id === response.entity.id
              ? {
                ...prevLinkQuestion,
                setting: {
                  ...prevLinkQuestion.setting,
                  mapConfig: {
                    ...prevLinkQuestion.setting.mapConfig,
                    style: style.name,
                  },
                },
                color: {
                  ...prevLinkQuestion.color,
                  mapLandColor: style.styleColors.landColor,
                  mapWaterColor: style.styleColors.waterColor,
                },
              }
              : prevLinkQuestion;

          return updatedLinkQuestion;
        });

        return updatedQuestions;
      });
    }
  }

  async function onTTStyleChange(value) {
    const updatedSetting = {
      ...currentRosFeature.setting,
      transientThoughtDisplayStyle: value
    };

    setCurrentRosFeature((prev) => ({ ...prev, setting: updatedSetting }));
    setShowTTStylePopup(false);

    const response = await updateQuestion(currentRosFeature.id, { setting: updatedSetting });

    if (isPreview) return;
    await updateEngagement(featureId, 'transientThought', { settings: updatedSetting });

    if (response.status) {
      setLinkQuestions((prev) =>
        prev.map((q) =>
          q.id === response.entity.id
            ? { ...q, setting: { ...q.setting, transientThoughtDisplayStyle: value } }
            : q
        )
      );
    }
  }

  return (
    <div
      className={`${colors.containerColor} flex items-center justify-center gap-2  rounded-full ${!isPreview ? 'ml-4' : ''} cursor-pointer p-0.5`}>
      {showPlayIcon && (
        <Tooltip
          content="Resume"
          placement="top"
        >
          <div
            id='tooltip'
            className={`group flex items-center justify-center rounded-full w-12 h-12 ${colors.hoverBg} ${isDisabled ? 'cursor-not-allowed' : ''}`}
            onClick={isDisabled ? undefined : handlePlayIcon}
          >
            <PlayArrowRoundedIcon className={`${colors.textColor} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''} transition-none!`} size={layoutMode === 'square' ? 20 : 24} />
          </div>
        </Tooltip>
      )}
      {showPauseIcon && (
        <Tooltip
          content="Pause"
          placement="top"
        >
          <div
            id='tooltip'
            className={`group flex items-center justify-center rounded-full w-12 h-12 ${colors.hoverBg} ${isDisabled ? 'cursor-not-allowed' : ''}`}
            onClick={isDisabled ? undefined : handlePauseIcon}
          >
            <PauseRoundedIcon className={`${colors.textColor} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''} transition-none!`} size={layoutMode === 'square' ? 20 : 24} />
          </div>
        </Tooltip>
      )}
      {showReplayIcon && (
        <Tooltip
          content={replayIconText}
          placement="top"
        >
          <div
            id='tooltip'
            className={`group flex items-center justify-center rounded-full w-12 h-12 ${colors.hoverBg} ${isDisabled ? 'cursor-not-allowed' : ''}`}
            onClick={isDisabled ? undefined : handleReplayIcon}
          >
            <ReplayIcon className={`${colors.textColor} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''} transition-none!`} size={layoutMode === 'square' ? 20 : 24} />
          </div>
        </Tooltip>
      )}

      <div className='relative'>
        {currentRosFeature?.type === 'transientThought' && (
          <Popover
            isOpen={!!showTTStylePopup}
            onOpenChange={(open) => setShowTTStylePopup(open)}
            placement='top-start'
            showArrow
            isTriggerDisabled={isDisabled}
            classNames={{ base: ['before:bg-grey-800'] }}
          >
            <PopoverTrigger>
              <div>
                <Tooltip content="Display style" placement="top">
                  <div
                    id='tooltip'
                    className={`group flex items-center justify-center rounded-full w-10 h-10 ${showTTStylePopup ? 'bg-white' : colors.hoverBg} ${isDisabled ? 'cursor-not-allowed' : ''}`}
                  >
                    <LayersRoundedIcon
                      className={`${showTTStylePopup ? 'text-grey-800' : colors.textColor} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''} transition-none!`}
                      size={layoutMode === 'square' ? 20 : 24}
                    />
                  </div>
                </Tooltip>
              </div>
            </PopoverTrigger>
            <PopoverContent className='p-0 m-0'>
              <div
                className='rounded-lg bg-grey-800 shadow-md cursor-default flex gap-2 p-3'
                style={{ zoom: Math.min(1, Math.max(0.5, windowWidth / DESIGN_WIDTH)) }}
              >
                {[
                  { value: 'cloud', label: 'Thought Cloud' },
                  { value: 'bubbles', label: 'Chat Bubble' },
                ].map(({ value, label }) => {
                  const isSelected = (currentRosFeature?.setting?.transientThoughtDisplayStyle || 'cloud') === value;
                  return (
                    <button
                      key={value}
                      onClick={() => onTTStyleChange(value)}
                      className={`flex flex-col items-center gap-2 p-2 rounded-lg border ${isSelected ? 'border-red-500' : 'border-transparent'}`}
                    >
                      <div className='w-[114px] h-[64px] flex items-center justify-center bg-grey-700 rounded-lg overflow-hidden'>
                        {value === 'cloud'
                          ? React.cloneElement(cloudVariants['small-tl']('#FABBFF'), { width: 100, height: 56 })
                          : React.cloneElement(bubbleVariants['small-tl']('#F26A60'), { width: 100, height: 48 })}
                      </div>
                      <p className='text-sm text-white'>{label}</p>
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        )}
        {currentRosFeature?.type === 'map' && <>
          <div className="flex flex-col items-center relative">
            {onboardingSettings?.SHOW_ONBOARDING_ICON_GUIDE && (<>
              <div className="flex absolute right-[40px] bottom-[76px]">
                <TooltipBox text="Change map styles" />
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="29"
                height="55"
                viewBox="0 0 29 55"
                fill="none"
                className="absolute left-0 -top-[60px] z-50"
              >
                <path
                  d="M25.8626 48.7368C24.3899 48.7237 23.1855 49.907 23.1725 51.3798C23.1595 52.8525 24.3428 54.0569 25.8155 54.0699C27.2882 54.0829 28.4926 52.8996 28.5056 51.4269C28.5186 49.9542 27.3353 48.7498 25.8626 48.7368ZM0.589304 1.83226L13.7224 1.86755L13.7251 0.86755L0.591991 0.832266L0.589304 1.83226ZM25.1914 13.3229L25.3391 51.4053L26.3391 51.4014L26.1914 13.319L25.1914 13.3229ZM13.7224 1.86755C20.0442 1.88453 25.1669 7.00115 25.1914 13.3229L26.1914 13.319C26.1647 6.44755 20.5966 0.886012 13.7251 0.86755L13.7224 1.86755Z"
                  fill="white"
                />
              </svg>
            </>)}

            <Popover
              isOpen={!!showMapStylePopup}
              onOpenChange={(open) => setShowMapStylePopup(open)}
              placement='top-start'
              showArrow
              isTriggerDisabled={isDisabled}
              classNames={{
                base: [
                  // arrow color
                  'before:bg-grey-800',
                ],
              }}>
              <PopoverTrigger>
                <div>
                  <Tooltip content="Map styles" placement="top">
                    <div
                      id='tooltip'
                      className={`group flex items-center justify-center rounded-full w-10 h-10 ${showMapStylePopup ? 'bg-white' : colors.hoverBg} ${isDisabled ? 'cursor-not-allowed' : ''}`}
                    >
                      <LayersRoundedIcon className={`${showMapStylePopup ? 'text-grey-800' : colors.textColor} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''} transition-none!`} size={layoutMode === 'square' ? 20 : 24} />
                    </div>
                  </Tooltip>
                </div>
              </PopoverTrigger>
              <PopoverContent className='p-0 m-0'>
                <div
                  className={`rounded-lg bg-grey-800 shadow-md cursor-default grid gap-2 z-9999
${layoutMode === 'square' || showInstantCustomisationPopup ? 'grid-cols-2' : 'grid-cols-3'}`}
                  style={{ width: layoutMode === 'square' || showInstantCustomisationPopup ? 450 : 700, zoom: Math.min(1, Math.max(0.5, windowWidth / DESIGN_WIDTH)) }}>
                  {mapStyles.map(style => (
                    <button
                      key={style.value}
                      onClick={() => onMapStyleChange(style)}
                      className={`flex items-center gap-2 p-2 rounded-lg ${mapStyle === style.value ? 'border border-red-500' : ''}`}>
                      <div className="w-[114px] h-[64px]">
                        <img src={style.url} alt={style.name} className='size-full object-cover rounded-lg bg-grey-300' />
                      </div>
                      <p className='text-sm text-white'>{style.name}</p>
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </>}
      </div>

      <Tooltip
        content="Start timer"
        placement="top"
      >
        <div
          id='tooltip'
          className={`group flex items-center justify-center rounded-full w-12 h-12 ${colors.hoverBg} ${isDisabled ? 'cursor-not-allowed' : ''}`}
          onClick={isDisabled ? undefined : () => setShowTimer(true)}
        >
          <TimerOutlinedIcon className={`${colors.textColor} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''} transition-none!`} size={layoutMode === 'square' ? 20 : 24} />
        </div>
      </Tooltip>

      {!(isPowerpoint || isZoomApp || isInIframe) && (
        <Tooltip
          content={isFullScreen ? 'Exit full screen' : 'Full screen'}
          placement="top"
        >
          <div
            id='tooltip'
            className={`group flex items-center justify-center rounded-full w-12 h-12 ${colors.hoverBg} ${isDisabled ? 'cursor-not-allowed' : ''}`}
            onClick={isDisabled ? undefined : () => isFullScreen ? handleFullScreen(false) : handleFullScreen(true)}
          >
            {isFullScreen ? (
              <CloseFullscreenRoundedIcon className={`${colors.textColor} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''} transition-none!`} size={layoutMode === 'square' ? 20 : 24} />
            ) : (
              <OpenInFullRoundedIcon className={`${colors.textColor} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''} transition-none!`} size={layoutMode === 'square' ? 20 : 24} />
            )}
          </div>
        </Tooltip>
      )}
      {showQrCode && (
        <div className="flex flex-col items-center relative">
          {onboardingSettings?.SHOW_ONBOARDING_ICON_GUIDE && (<>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="30"
              height="55"
              viewBox="0 0 30 55"
              fill="none"
              className="absolute left-[20px] -top-[56px] z-50"
            >
              <path d="M3.34501 48.7368C4.81771 48.7237 6.02212 49.907 6.03514 51.3798C6.04816 52.8525 4.86485 54.0569 3.39215 54.0699C1.91944 54.0829 0.715031 52.8996 0.702014 51.4269C0.688997 49.9542 1.8723 48.7498 3.34501 48.7368ZM29.6189 1.82342L15.5015 1.8646L15.4986 0.864608L29.616 0.823423L29.6189 1.82342ZM4.03515 13.3143L3.86857 51.4055L2.86858 51.4011L3.03516 13.3099L4.03515 13.3143ZM15.5015 1.8646C9.18295 1.88304 4.06278 6.99577 4.03515 13.3143L3.03516 13.3099C3.06519 6.44196 8.6306 0.884644 15.4986 0.864608L15.5015 1.8646Z" fill="white" />
            </svg>
            <div className="flex absolute left-[40px] bottom-[76px]">
              <TooltipBox text="Share joining instructions" />
            </div>
          </>)}
          <Tooltip
            content="Show joining instructions"
            placement="top"
          >
            <div
              id='qr-code-icon-btn'
              className={`group flex items-center justify-center rounded-full w-12 h-12 ${colors.hoverBg} ${isDisabled ? 'cursor-not-allowed' : ''}`}
              onClick={isDisabled ? undefined : handleQrCode}
            >
              <QrCodeSharpIcon className={`${colors.textColor} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''} transition-none!`} size={layoutMode === 'square' ? 20 : 24} />
              {showInstructions && (
                <div className="relative flex items-center">
                  <div className="absolute bottom-16 right-8 w-[206px] px-4 py-4 bg-white rounded-lg inline-flex justify-center items-center gap-2">
                    <div className="text-black text-sm font-normal leading-5">Show joining instructions</div>
                  </div>
                  <div className="absolute bottom-[10px] -left-[38px]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="29" height="79" viewBox="0 0 29 79" fill="none">
                      <path d="M26.1396 73.1813C24.6669 73.1683 23.4625 74.3516 23.4495 75.8243C23.4365 77.297 24.6198 78.5014 26.0925 78.5144C27.5652 78.5275 28.7696 77.3442 28.7826 75.8715C28.7956 74.3987 27.6123 73.1943 26.1396 73.1813ZM26.1197 12.6128L25.6197 12.6128L26.1197 12.6128ZM0.089571 1.11309L14.1189 1.11209L14.1188 0.112088L0.0895006 0.113085L0.089571 1.11309ZM25.6197 12.6128L25.6161 75.8479L26.6161 75.8479L26.6197 12.6128L25.6197 12.6128ZM14.1189 1.11209C20.4708 1.11164 25.6201 6.2609 25.6197 12.6128L26.6197 12.6128C26.6201 5.70862 21.023 0.111597 14.1188 0.112088L14.1189 1.11209Z" fill="white" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          </Tooltip>
        </ div>
      )}
    </div>
  );
};

export default ActionToolbar;
